import React, { useEffect, useState } from "react";
import { ButtonBase, TwoColumnSectionBase } from "@reusejs/react";
import { useRouter } from "next/router";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import Link from "next/link";
import {
  getBidderByUserUuidAndEventUuid,
  addBidderForEvent,
  getBidderForEvent,
} from "services";

export interface AuctionEventCardProps {
  property: any;
  event: any;
  type: string;
}

export default function AuctionEventCard(props: AuctionEventCardProps) {
  const router = useRouter();

  return (
    <TwoColumnSectionBase
      firstColumnEl={<Content {...props} />}
      secondColumnEl={<Action {...props} />}
      twoColumnSectionBaseClasses={{
        firstElWrapperStyles: "w-full md:w-3/4 self-stretch",
        secondElWrapperStyles: "w-full md:w-1/4 border-l self-stretch p-4",
        columnsFlexWrapperStyles:
          "flex items-start justify-between border shadow rounded-lg flex-col md:flex-row bg-white",
      }}
    />
  );
}

const Content = (props: any) => {
  const getStatusItem = (status: string) => {
    let element;
    switch (status) {
      case "live":
        element = (
          <div className="text-left px-3 sm:px-4 py-2 bg-blue-100 sm:rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-blue-500">
                Status:{" "}
                <span className="font-semibold">Auction event is live.</span>
              </div>
            </div>
          </div>
        );
        break;

      case "end":
        element = (
          <div className="text-left px-3 sm:px-4 py-2 bg-red-100 sm:rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-red-500">
                Status:{" "}
                <span className="font-semibold">Auction event has ended.</span>
              </div>
            </div>
          </div>
        );
        break;

      default:
        break;
    }
    return element;
  };

  return (
    <div className="flex-1">
      <div className="text-left px-3 sm:px-4 py-2 border-b text-xs text-gray-500">
        Event ID: {props.event.listing_id}
      </div>
      <div className="text-left border-b px-3 sm:px-4 sm:flex sm:justify-between">
        <div className="text-left basis-1/2 py-4">
          <div className="flex flex-col">
            <div className="text-xs text-gray-500">Event Name</div>
            <div className="text-sm break-all">{props.event.event_name}</div>
          </div>
          <div className="flex flex-col mt-4">
            <div className="text-xs text-gray-500">State</div>
            <div className="text-sm capitalize">
              {props.event?.property_dump?.state?.name}
            </div>
          </div>
        </div>
        <div className="text-left basis-1/2 px-4 py-4 sm:pl-6 ">
          <div className="flex flex-col">
            <div className="text-xs text-gray-500">Reserve Price</div>
            <div className="text-sm">
              {props.event?.property_dump?.reserve_price}
            </div>
          </div>
          <div className="flex flex-col mt-4">
            <div className="text-xs text-gray-500">EMD Amount</div>
            <div className="text-sm capitalize">
              {props.event?.property_dump?.emd_amount}
            </div>
          </div>
        </div>
      </div>
      <div className="text-left border-b px-3 sm:px-4 sm:flex sm:justify-between">
        <div className="flex basis-1/2 sm:border-r py-4 flex-col">
          <div className="text-xs text-gray-500">
            Bidding Start Date and Time
          </div>
          <div className="text-sm pt-1">
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3">
                {props.event.start_date_time &&
                  DateTime.fromISO(props.event.start_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")}
              </div>
              <div className="px-3 text-xs">
                {props.event.start_date_time &&
                  `${DateTime.fromISO(props.event.start_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          </div>
        </div>
        <div className="flex basis-1/2 py-4 flex-col sm:pl-6">
          <div className="text-xs text-gray-500">Bidding End Date and Time</div>
          <div className="text-sm pt-1">
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3">
                {props.event.end_date_time &&
                  DateTime.fromISO(props.event.end_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")}
              </div>
              <div className="px-3 text-xs">
                {props.event.end_date_time &&
                  `${DateTime.fromISO(props.event.end_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          </div>
        </div>
      </div>
      {getStatusItem(props.event.status)}
    </div>
  );
};

const Action = (props: any) => {
  const router = useRouter();
  const [bidder, setBidder] = useState<any>(null);
  const user = useSelector((state: any) => {
    return state.bidderAuth.user;
  });
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });

  const getStatusItem = (status: string) => {
    let element;

    switch (status) {
      case "live":
        element = <div className="text-2xl font-bold text-blue-500">Live</div>;
        break;

      case "end":
        element = <div className="text-2xl font-bold text-red-500">Ended</div>;
        break;

      default:
        break;
    }
    return element;
  };

  useEffect(() => {
    (async () => {
      if (props?.event.uuid && user.uuid) {
        const res: any = await getBidderByUserUuidAndEventUuid(
          user.uuid,
          props.event?.uuid,
          ""
        );
        setBidder(res[0]);
      }
    })();
  }, [props, user]);

  const handleBidderVerification = async () => {
    // create bidder for the event first and then redirect
    try {
      const bidders: any = await getBidderForEvent(
        { per_page: 999, page: 1 },
        props.event.uuid
      );
      const allBiddersForEvent = bidders.data;
      const foundBidder = allBiddersForEvent.find(
        (item: any) => item.user_uuid === user.uuid
      );

      console.log("bidders::>", bidders, foundBidder);

      if (!foundBidder) {
        let payload: any = {
          email: onboardingData.email,
          name: onboardingData.name,
        };
        console.log("props.event:::>", props.event);
        payload["event_uuid"] = props.event.uuid;
        payload["status"] = "pending";
        payload["meta"] = {
          //     banker_email: banker?.attributes[0]?.value,
          //     bank_name: bank?.name,
          approver_email:
            props.event?.meta?.approver &&
            props.event?.meta?.approver.length > 0
              ? props.event?.meta?.approver[0]?.email
              : "",
          approver_name:
            props.event?.meta?.approver &&
            props.event?.meta?.approver.length > 0
              ? props.event?.meta?.approver[0]?.name
                ? props.event?.meta?.approver[0]?.name
                : props.event?.meta?.approver[0]?.email
              : "",
          branch_name: props.event?.branch?.name,
          event_name: props.event?.event_name,
          dashboard_url: `${process.env.NEXT_PUBLIC_WEB_URL}auction-events`,
          event_id: props.event.listing_id,
          auction_start_datetime: DateTime.fromISO(props.event?.start_date_time)
            .setZone("Asia/Kolkata")
            .toFormat("dd MMMM yyyy hh:mm a"),
          emd_last_datetime: DateTime.fromISO(
            props.event?.property_dump?.emd_last_date
          )
            .setZone("Asia/Kolkata")
            .toFormat("dd MMMM yyyy hh:mm a"),
          //     name: payload.name,
          //     pan: payload.pan,
        };
        const res: any = await addBidderForEvent(payload);
        console.log("res::::>", res);
        if (res && res.uuid) {
          router.push(`/kyc/${res.uuid}?step=verify_identities`);
        }
      } else {
        router.push(`/kyc/${foundBidder.uuid}?step=verify_identities`);
      }
    } catch (error: any) {
      console.log("error", error);
    }
  };

  return (
    <div className="flex flex-col h-full justify-between">
      <div>
        <div className="text-left flex justify-center">
          <div className="flex basis-1/2 flex-col items-center">
            {getStatusItem(props.event.status)}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {process.env.NEXT_PUBLIC_ENABLE_FEATURE === "true" && (
          <div>
            {bidder && bidder?.status === "verified" ? (
              <div className="text-2xl text-center font-bold text-green-500">
                KYC Verified
              </div>
            ) : (
              <ButtonBase
                variant="primary"
                type="submit"
                label={"Complete KYC"}
                onClick={() => handleBidderVerification()}
                buttonBaseClasses={{
                  font: "text-base font-medium",
                  backgroundColor: "bg-blue-600 hover:bg-blue-700",
                  textColor: "text-white",
                  border: "any",
                  width: "w-full",
                  padding: "px-4 py-3",
                }}
              />
            )}
          </div>
        )}
        <ButtonBase
          variant="primary"
          type="submit"
          label={"View Property"}
          onClick={() =>
            window.open(
              `${process.env.NEXT_PUBLIC_WEB_URL}auction-properties/${props.event?.property_dump?.listing_id}`,
              "_blank"
            )
          }
          buttonBaseClasses={{
            font: "text-base font-medium",
            backgroundColor: "bg-transparent",
            textColor: "text-gray-900 hover:text-blue-600",
            border: "border border-gray-900 hover:border-blue-600",
            width: "w-full",
            padding: "px-4 py-3",
          }}
        />
        {bidder &&
          bidder?.status === "verified" &&
          props.type !== "allEvents" &&
          (props.event?.status === "end" ||
          props.event?.status === "cancelled" ? (
            <ButtonBase
              variant="primary"
              type="submit"
              label={
                props.event?.status === "end" ||
                props.event?.status === "cancelled"
                  ? "View Details"
                  : "Start Bidding"
              }
              onClick={() =>
                router.push(`/auction-events/${props.event?.uuid}`)
              }
              buttonBaseClasses={{
                font: "text-base font-medium",
                backgroundColor: "bg-blue-600 hover:bg-blue-700",
                textColor: "text-white",
                border: "any",
                width: "w-full",
                padding: "px-4 py-3",
              }}
              disabled={["pending", "accepted"].includes(props.event?.status)}
            />
          ) : (
            <Link href={`/auction-events/${props.event?.uuid}`}>
              <a
                target={"_blank"}
                className="inline-flex justify-center items-center focus:outline-none text-base font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-3 any w-full disabled:opacity-50 dark:disabled:opacity-80"
              >
                Start Bidding
              </a>
            </Link>
          ))}
      </div>
    </div>
  );
};
